import Icon1 from "@assets/icons/Icon";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import React, { useRef, useEffect } from "react";
import { createPortal } from "react-dom";

interface DeleteUserModalProps {
  userName: string;
  onConfirm: () => void;
  onClose: () => void;
  isDeleting?: boolean;
}

export default function DeleteUserModal({
  userName,
  onConfirm,
  onClose,
  isDeleting = false,
}: DeleteUserModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isDeleting) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [isDeleting, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const modalContent = (
    <div
      className="fixed inset-0 z-50 bg-shadow_50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isDeleting) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="bg-white border-1 border-neutral-300 rounded-2xl w-full max-w-md flex flex-col"
      >
        {/* Header */}
        <div className="flex flex-row gap-4 p-6 pb-4 items-start">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <Icon1 name="trash-can" size={20} color="rgb(239 68 68)" />
          </div>
          <div className="flex-1">
            <div className="text-h2 font-semibold text-black mb-2">
              Supprimer ce membre
            </div>
            <div className="text-h3 text-neutral-600">
              Êtes-vous sûr de vouloir supprimer{" "}
              <span className="font-semibold">{userName}</span> ? Cette action
              est irréversible et le membre perdra immédiatement l'accès à
              l'organisation.
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-row gap-3 p-6 pt-4 justify-end border-t-1 border-neutral-200">
          <SecondaryButton
            label="Annuler"
            onClick={onClose}
            variant="small"
            disabled={isDeleting}
          />
          <SecondaryButton
            label={isDeleting ? "Suppression..." : "Supprimer"}
            onClick={onConfirm}
            variant="small"
            danger
            disabled={isDeleting}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
